import { Col, Container, Row, Space } from '@ag1/ui';
import { viewContentEvent } from '@portal/analytics/events';
import { isMobileTablet } from '@portal/shared/utils/devices';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useStoreCurrency } from '@portal/state/hooks/useStoreCurrency';
import { useProductStore } from '@portal/state/store/pdpStore';
import { OfferSubscriptionMediaType, PDPType } from '@portal/types/offer';
import { useRouter } from 'next/router';
import { ReactNode, useEffect } from 'react';
import { getCurrentPriceMetafield } from '@portal/shared/utils/shop/bundle/metafields';
import { PLAN_TYPES } from '../PriceSelector/PriceSelector';
import { getSelectedVariant } from '../utils';
import styles from './ProductDetailPageWrapper.module.scss';

type ProductDetailPageWrapperProps = {
  leftSections: ReactNode;
  rightSections: ReactNode;
};

export const ProductDetailPageWrapper = ({
  leftSections,
  rightSections
}: ProductDetailPageWrapperProps) => {
  const router = useRouter();
  const { query } = router;
  const store = useStoreValue();
  const {
    variants,
    selectedVariantId,
    bundleData,
    pdpType,
    flavor,
    lastViewContentId,
    isOtpProduct,
    setLastViewContentId
  } = useProductStore();
  const { currency } = useStoreCurrency();

  // Fire ViewContent
  useEffect(() => {
    // Helper function to determine whether or not to fire new view content ID
    // Adjust this to handle more permutations if needed
    const generateViewContentId = (id: string, isOtp: boolean) => `${id}-${isOtp}`;

    const isOtp = query.plan === PLAN_TYPES.ONE_TIME || isOtpProduct;
    const isBundlePdP = pdpType === 'bundleView';
    const idToCheck = isBundlePdP ? bundleData?.id || '' : selectedVariantId;
    const newId = generateViewContentId(idToCheck, isOtp);

    // Wait for valid data or viewContent already fired for this ID
    if (!router.isReady || newId === lastViewContentId) return;

    let contents;
    let finalPrice;

    if (isBundlePdP && bundleData) {
      // Use current bundle data
      // Parse price without currency symbol
      const originalPrice = getCurrentPriceMetafield(bundleData)?.value.replaceAll('$', '');
      finalPrice = parseFloat(originalPrice ?? '0');
      contents = [
        {
          id: bundleData.id || '',
          title: bundleData.title || '',
          category: PDPType.Bundle,
          price: finalPrice,
          quantity: 1
        }
      ];
    } else if (!isBundlePdP && variants) {
      // Use selected PDP data
      const variant = getSelectedVariant(variants, selectedVariantId, store, flavor);
      if (!variant) return;

      const { oneTimePrice, oneTimeVariantId, price, quantity, subscriptionVariantIds, title } =
        variant;
      const variantId = isOtp
        ? oneTimeVariantId?.[0]?.variantId
        : subscriptionVariantIds?.[0]?.variantId;
      finalPrice = parseFloat((isOtp ? oneTimePrice : price) ?? '0');
      contents = [
        {
          id: variantId || '',
          title: title || '',
          category: PDPType.Product,
          price: finalPrice,
          quantity
        }
      ];
    }
    if (!contents) return;

    viewContentEvent({
      contents,
      eventId: Date.now().toString(),
      currency,
      value: finalPrice,
      deviceType: isMobileTablet()
        ? OfferSubscriptionMediaType.Mobile
        : OfferSubscriptionMediaType.Desktop
    });
    setLastViewContentId(newId);
  }, [
    router.isReady,
    lastViewContentId,
    isOtpProduct,
    query.plan,
    selectedVariantId,
    pdpType,
    variants,
    bundleData,
    currency,
    setLastViewContentId,
    store,
    flavor
  ]);

  useEffect(() => {
    // Reset lastViewContent if unmounting
    return () => {
      setLastViewContentId('');
    };
  }, [setLastViewContentId]);

  return (
    <Container narrow>
      <Space
        top={{ xxs: 0, s: 16, l: 16 }}
        bottom={{ xxs: 40, s: 56, l: 60 }}
        className={styles.root}>
        <Row>
          <Col className={styles.columnLeft} span={{ xxs: 12, m: 6 }}>
            {leftSections}
          </Col>
          <Col className={styles.columnRight} offset={{ l: 1 }} span={{ xxs: 12, m: 6, l: 5 }}>
            {rightSections}
          </Col>
        </Row>
      </Space>
    </Container>
  );
};
